// DegreeMonthlyComparisonWidget.js
import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from './api';

const DegreeMonthlyComparisonWidget = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  useEffect(() => {
    fetchDegreeDaysData();
  }, []);
  
  const fetchDegreeDaysData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current energy year
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      
      const currentEnergyYear = currentMonth >= 7 ? 
        `${currentYear}-${currentYear+1}` : 
        `${currentYear-1}-${currentYear}`;
        
      const previousEnergyYear = getPreviousEnergyYear();
      
      console.log('Current energy year:', currentEnergyYear);
      console.log('Previous energy year:', previousEnergyYear);
      
      // Fetch both current and previous year data
      const [currentResponse, prevResponse] = await Promise.all([
        api.get('/api/degree-days/monthly', {
          params: { energyYear: currentEnergyYear }
        }),
        api.get('/api/degree-days/monthly', {
          params: { energyYear: previousEnergyYear }
        })
      ]);
      
      // Process the API response into chart data
      const monthsOrder = [
        '07', '08', '09', '10', '11', '12', '01', '02', '03', '04', '05', '06'
      ];
      
      // Organize data by month CORRECTLY - the issue is here
      const currentYearData = {};
      currentResponse.data.forEach(item => {
        const fullMonth = item._id; // This is already in YYYY-MM format
        const month = fullMonth.split('-')[1]; // Get the MM part
        currentYearData[month] = item.totalDegreeDays;
      });
      
      const prevYearData = {};
      prevResponse.data.forEach(item => {
        const fullMonth = item._id; // This is already in YYYY-MM format
        const month = fullMonth.split('-')[1]; // Get the MM part
        prevYearData[month] = item.totalDegreeDays;
      });
      
      // Create chart data array with Dutch month names
      const chartData = monthsOrder.map(month => {
        // Map month numbers to Dutch names
        const monthNames = {
          '01': 'Jan', '02': 'Feb', '03': 'Mrt', '04': 'Apr',
          '05': 'Mei', '06': 'Jun', '07': 'Jul', '08': 'Aug',
          '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dec'
        };
        
        return {
          name: monthNames[month],
          vorigJaar: prevYearData[month] || 0,
          huidigJaar: currentYearData[month] || 0
        };
      });
      
      setChartData(chartData);
      setLastUpdate(new Date().toLocaleString());
      
    } catch (err) {
      console.error('❌ Error fetching degree days data:', err);
      setError('Kon de graaddagen niet laden. Probeer het later nog eens.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to get the previous energy year
  const getPreviousEnergyYear = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    
    const currentEnergyYearStart = currentMonth >= 7 ? currentYear : currentYear - 1;
    const previousEnergyYearStart = currentEnergyYearStart - 1;
    
    return `${previousEnergyYearStart}-${previousEnergyYearStart + 1}`;
  };
  
  if (loading) {
    return (
      <div className="degree-days-widget card">
        <h3>🌡️ Graaddagen per Maand</h3>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Gegevens laden...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="degree-days-widget card">
        <h3>🌡️ Graaddagen per Maand</h3>
        <div className="error-message">
          <p>{error}</p>
          <button className="sync-button" onClick={fetchDegreeDaysData}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="degree-days-widget">
      <h3>
        🌡️ Graaddagen per Maand
      </h3>
      
      {lastUpdate && (
        <div className="last-update">
          Laatste update: {lastUpdate}
          <button className="sync-button small" onClick={fetchDegreeDaysData} disabled={loading}>
            {loading ? 'Bezig...' : '🔄'}
          </button>
        </div>
      )}
      
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip formatter={(value) => Math.round(value) + ' graaddagen'} />
            <Legend />
            <Bar dataKey="vorigJaar" name="Vorig jaar" fill="#3B82F6" />
            <Bar dataKey="huidigJaar" name="Dit jaar" fill="#10B981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="widget-footer">
        <span>Teff = TG - 2/3 * FG • Gewogen graaddagen (14 - Teff)</span>
      </div>
    </div>
  );
};

export default DegreeMonthlyComparisonWidget;